import { useState } from 'react';
import { AlertCircle, MessageSquare, X } from 'lucide-react';

import { feedbackApi } from '../services/api';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  page: string;
  taxYear: number;
}

const STEP_OPTIONS = [
  { value: 'upload', label: 'Upload' },
  { value: 'questionnaire', label: 'Questionnaire' },
  { value: 'review', label: 'Review' },
  { value: 'forms', label: 'Forms' },
  { value: 'other', label: 'Other' },
];

export default function FeedbackModal({
  isOpen,
  onClose,
  page,
  taxYear,
}: FeedbackModalProps) {
  const [step, setStep] = useState('other');
  const [whatHappened, setWhatHappened] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [documentTypes, setDocumentTypes] = useState('');
  const [isBlocker, setIsBlocker] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  if (!isOpen) return null;

  const resetAndClose = () => {
    setStep('other');
    setWhatHappened('');
    setExpectedBehavior('');
    setDocumentTypes('');
    setIsBlocker(false);
    setSubmitting(false);
    setStatus('idle');
    onClose();
  };

  const handleSubmit = async () => {
    if (whatHappened.trim().length < 5) return;

    setSubmitting(true);
    setStatus('idle');
    try {
      await feedbackApi.submit({
        step,
        what_happened: whatHappened.trim(),
        expected_behavior: expectedBehavior.trim() || undefined,
        document_types: documentTypes
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean),
        is_blocker: isBlocker,
        page,
        metadata: {
          tax_year: taxYear,
        },
      });
      setStatus('success');
      window.setTimeout(() => {
        resetAndClose();
      }, 800);
    } catch {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl border border-gray-200">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-gray-700" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Report an issue</h2>
              <p className="text-sm text-gray-500">Send structured beta feedback without leaving the app.</p>
            </div>
          </div>
          <button
            onClick={resetAndClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            disabled={submitting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Which step?</span>
              <select
                value={step}
                onChange={(e) => setStep(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                {STEP_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2 mt-6 sm:mt-0">
              <input
                type="checkbox"
                checked={isBlocker}
                onChange={(e) => setIsBlocker(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">This blocked me from continuing</span>
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">What happened?</span>
            <textarea
              value={whatHappened}
              onChange={(e) => setWhatHappened(e.target.value)}
              rows={4}
              placeholder="Describe the issue, confusion, or incorrect behavior."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">What did you expect instead?</span>
            <textarea
              value={expectedBehavior}
              onChange={(e) => setExpectedBehavior(e.target.value)}
              rows={3}
              placeholder="Optional, but very helpful."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Document types involved</span>
            <input
              value={documentTypes}
              onChange={(e) => setDocumentTypes(e.target.value)}
              placeholder="Optional, e.g. W-2, 1099-INT"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </label>

          {status === 'error' && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>We couldn&apos;t send that just now. Please try again.</span>
            </div>
          )}

          {status === 'success' && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              Feedback submitted. Thank you.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <button
            onClick={resetAndClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || whatHappened.trim().length < 5}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Sending...' : 'Submit feedback'}
          </button>
        </div>
      </div>
    </div>
  );
}
